import type { CustomizationOptions, WasteReduction, RemainingMaterial, SuggestedProduct } from '../types';
import { SLAB_SPECIFICATIONS } from '../data/customization';

// Price per slab is 999€
const SLAB_PRICE = 999;

const PRODUCT_TEMPLATES: SuggestedProduct[] = [
  {
    type: 'side-table',
    name: 'Side Table Top',
    dimensions: { width: 500, length: 500 },
    area: 0.25,
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
    description: 'Perfect for a modern side table or nightstand',
    estimatedValue: SLAB_PRICE * (0.25 / SLAB_SPECIFICATIONS.standardArea) // Price proportional to area
  },
  {
    type: 'coffee-table',
    name: 'Coffee Table Top',
    dimensions: { width: 800, length: 1200 },
    area: 0.96,
    image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
    description: 'Elegant coffee table surface',
    estimatedValue: SLAB_PRICE * (0.96 / SLAB_SPECIFICATIONS.standardArea)
  },
  {
    type: 'shelf',
    name: 'Floating Shelf',
    dimensions: { width: 300, length: 1200 },
    area: 0.36,
    image: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
    description: 'Modern floating shelf solution',
    estimatedValue: SLAB_PRICE * (0.36 / SLAB_SPECIFICATIONS.standardArea)
  },
  {
    type: 'cutting-board',
    name: 'Decorative Cutting Board',
    dimensions: { width: 400, length: 600 },
    area: 0.24,
    image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800',
    description: 'Luxury serving and cutting board',
    estimatedValue: SLAB_PRICE * (0.24 / SLAB_SPECIFICATIONS.standardArea)
  }
];

function calculateRemainingPieces(
  mainPieceWidth: number,
  mainPieceLength: number,
  usedProducts: SuggestedProduct[] = []
): RemainingMaterial[] {
  let remainingPieces: RemainingMaterial[] = [];

  // Calculate initial remaining pieces
  if (SLAB_SPECIFICATIONS.dimensions.length > mainPieceLength) {
    remainingPieces.push({
      width: SLAB_SPECIFICATIONS.dimensions.width,
      length: SLAB_SPECIFICATIONS.dimensions.length - mainPieceLength,
      area: (SLAB_SPECIFICATIONS.dimensions.width * (SLAB_SPECIFICATIONS.dimensions.length - mainPieceLength)) / 1000000
    });
  }

  if (SLAB_SPECIFICATIONS.dimensions.width > mainPieceWidth) {
    remainingPieces.push({
      width: SLAB_SPECIFICATIONS.dimensions.width - mainPieceWidth,
      length: mainPieceLength,
      area: ((SLAB_SPECIFICATIONS.dimensions.width - mainPieceWidth) * mainPieceLength) / 1000000
    });
  }

  // Subtract used products from remaining pieces
  usedProducts.forEach(product => {
    // Find the best fitting piece for this product
    const bestPieceIndex = remainingPieces.findIndex(piece => 
      (product.dimensions.width <= piece.width && product.dimensions.length <= piece.length) ||
      (product.dimensions.length <= piece.width && product.dimensions.width <= piece.length)
    );

    if (bestPieceIndex !== -1) {
      const piece = remainingPieces[bestPieceIndex];
      const productArea = (product.dimensions.width * product.dimensions.length) / 1000000;
      
      // Update the piece dimensions and area
      const newArea = piece.area - productArea;
      if (newArea > 0) {
        remainingPieces[bestPieceIndex] = {
          ...piece,
          area: newArea,
          // Adjust dimensions based on how the product fits
          width: piece.width - product.dimensions.width,
          length: piece.length
        };
      } else {
        // Remove the piece if no usable area remains
        remainingPieces.splice(bestPieceIndex, 1);
      }
    }
  });

  return remainingPieces;
}

function findSuggestedProducts(remainingPieces: RemainingMaterial[], usedProducts: SuggestedProduct[]): SuggestedProduct[] {
  const suggestions: SuggestedProduct[] = [];
  const usedTypes = new Set(usedProducts.map(p => p.type));

  remainingPieces.forEach(piece => {
    PRODUCT_TEMPLATES.forEach(template => {
      // Skip if this product type is already used
      if (usedTypes.has(template.type)) return;

      const canFitNormally = 
        template.dimensions.width <= piece.width &&
        template.dimensions.length <= piece.length;
      
      const canFitRotated = 
        template.dimensions.width <= piece.length &&
        template.dimensions.length <= piece.width;

      if ((canFitNormally || canFitRotated) && template.area <= piece.area) {
        if (!suggestions.some(s => s.type === template.type)) {
          suggestions.push({
            ...template,
            estimatedValue: SLAB_PRICE * (template.area / SLAB_SPECIFICATIONS.standardArea)
          });
        }
      }
    });
  });

  return suggestions;
}

export function calculateWasteReduction(
  options: CustomizationOptions,
  additionalProducts: SuggestedProduct[] = []
): WasteReduction {
  const mainPieceArea = (options.width * options.length) / 1000000; // Convert to m²
  const remainingPieces = calculateRemainingPieces(options.width, options.length, additionalProducts);
  const remainingArea = remainingPieces.reduce((total, piece) => total + piece.area, 0);
  const suggestedProducts = findSuggestedProducts(remainingPieces, additionalProducts);

  const usedArea = mainPieceArea + additionalProducts.reduce((total, product) => total + product.area, 0);

  return {
    originalArea: SLAB_SPECIFICATIONS.standardArea,
    usedArea,
    remainingArea,
    efficiency: (usedArea / SLAB_SPECIFICATIONS.standardArea) * 100,
    remainingPieces,
    suggestedProducts
  };
}